import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { gapAssessmentApi, GapAssessment } from '../api/gapAssessment.api';
import { useAuth } from '@contexts/AuthContext';
import ConfirmationModal from '@/components/ConfirmationModal';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
};

const ScoreBar: React.FC<{ score?: number }> = ({ score }) => {
  if (score === undefined || score === null) return <span className="text-xs text-gray-400">Not scored</span>;
  const pct = Math.round(score);
  const color = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-700">{pct}%</span>
    </div>
  );
};

const GapAssessmentList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<GapAssessment[]>([]);
  const [stats, setStats] = useState<{ total: number; draft: number; in_progress: number; completed: number; avg_score: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ title: '', framework_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; assessmentId: number | null; assessmentTitle: string }>({ open: false, assessmentId: null, assessmentTitle: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const companyId = user?.company_id;

  const confirmDeleteAssessment = async () => {
    if (!deleteConfirm.assessmentId) return;
    setDeleteLoading(true);
    try {
      await gapAssessmentApi.delete(deleteConfirm.assessmentId);
      setDeleteConfirm({ open: false, assessmentId: null, assessmentTitle: '' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const load = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError('');
    try {
      const [list, statsRes] = await Promise.all([
        gapAssessmentApi.list({ ...(statusFilter ? { status: statusFilter } : {}), company_id: companyId }),
        gapAssessmentApi.getStats(companyId),
      ]);
      setAssessments((list as any).assessments || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.framework_id || !companyId) return;
    setSubmitting(true);
    try {
      const assessment = await gapAssessmentApi.create({ company_id: companyId, framework_id: Number(form.framework_id), title: form.title });
      setCreateModal(false);
      setForm({ title: '', framework_id: '' });
      navigate(`/compliance/gap-assessment/${assessment.id}`);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Gap Assessments</h1>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setCreateModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> Start Assessment
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', value: stats.total },
            { label: 'Draft', value: stats.draft },
            { label: 'In Progress', value: stats.in_progress },
            { label: 'Completed', value: stats.completed },
            { label: 'Avg Score', value: `${(stats.avg_score || 0).toFixed(0)}%` },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-sm text-gray-500 mb-1">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {['', 'draft', 'in_progress', 'completed'].map(s => <option key={s} value={s}>{s ? s.replace('_', ' ') : 'All Statuses'}</option>)}
        </select>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : assessments.length === 0 ? (
          <div className="py-12 text-gray-500">No gap assessments found. Start one to identify compliance gaps.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Title', 'Framework', 'Score', 'Status', 'Created', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y">
              {assessments.map(a => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-left font-medium text-gray-900">{a.title}</td>
                  <td className="px-4 py-3 text-left text-gray-600">{a.framework_name || `Framework #${a.framework_id}`}</td>
                  <td className="px-4 py-3 text-left w-40"><ScoreBar {...(a.overall_score !== undefined ? { score: a.overall_score } : {})} /></td>
                  <td className="px-4 py-3 text-left">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[a.status]}`}>
                      {a.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left text-gray-600">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-left flex items-center gap-2">
                    <button onClick={() => navigate(`/compliance/gap-assessment/${a.id}`)}
                      className="text-blue-600 hover:underline text-xs">View</button>
                    <button
                      onClick={() => setDeleteConfirm({ open: true, assessmentId: a.id, assessmentTitle: a.title })}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete assessment"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmationModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, assessmentId: null, assessmentTitle: '' })}
        onConfirm={confirmDeleteAssessment}
        title="Delete Assessment"
        message={`Are you sure you want to delete "${deleteConfirm.assessmentTitle}"? This will remove all assessment items and cannot be undone.`}
        confirmText="Delete Assessment"
        cancelText="Cancel"
        type="danger"
        loading={deleteLoading}
      />

      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">Start Gap Assessment</h2>
            <input placeholder="Assessment Title *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
            <input placeholder="Framework ID *" type="number" value={form.framework_id} onChange={e => setForm({ ...form, framework_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
            <p className="text-xs text-gray-500">The assessment will auto-populate with all compliance pages for the selected framework.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleCreate} disabled={submitting || !form.title.trim() || !form.framework_id}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Start Assessment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GapAssessmentList;
